import { List, fromJS } from 'immutable'
import type { ReactElement } from 'react'
import { defaults } from 'lodash-es'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import cc from 'classcat'

import type { BlockAnimation } from './BlockAnimationSettings'
import { DragSource } from './utils/DragAndDrop'
import { blockSupportsHeadline, isHeadlineHidden } from './Workspace.helpers'
import { blockTypes } from './blocks'
import { emptyHeadline } from '../../../components/editorSidebarBlockTemplates'
import { getPlain } from '../../../store/utils'
import { themeGroupDelimiter } from '../../../../utils/themes'
import { useTranslate } from '../../../utils/translate'
import BlockAnimationSettings from './BlockAnimationSettings'
import BlockBackgroundSettings from './BlockBackgroundSettings'

export type BackgroundColorSettings = {
  backgroundColor: string
  colorOrGradient: 'color' | 'gradient'
  gradientStartColor: string
  gradientMidColor: string
  gradientEndColor: string
  gradientOrientation: '45deg' | '90deg' | '180deg'
  showGradientMidColor: boolean
}

export const defaultBackgroundColorSettings: BackgroundColorSettings = {
  backgroundColor: 'transparent',
  colorOrGradient: 'color',
  gradientStartColor: '#ffffff',
  gradientMidColor: '#828282',
  gradientEndColor: '#000000',
  gradientOrientation: '90deg',
  showGradientMidColor: false,
}

export type BackgroundWidthSettings = {
  width: 'content' | 'full'
  isUserModified?: boolean
}

export const getDefaultBackgroundWidthSettings = (themeName: string): BackgroundWidthSettings => {
  const [themeNamespace] = themeName.split(themeGroupDelimiter)

  switch (themeNamespace) {
    case 'epages.dimension':
    case 'epages.neutral':
    case 'epages.essence':
    case 'epages.uptown':
    case 'epages.structure':
      return { width: 'content' }
    case 'epages.vision':
    case 'epages.limelight':
    case 'epages.editorial':
      return { width: 'full' }
    default:
      return { width: 'content' }
  }
}

type Props = {
  block: ImmutableMap
  blockPosition: number
  isDragActive: boolean
  config: any
  editorView: boolean
  onChange: (block: ImmutableMap) => void
  onDelete: () => void
  onDuplicate: (block: ImmutableMap, newPosition: number) => void
}

export default function Block({
  block,
  blockPosition,
  isDragActive,
  config,
  editorView,
  onChange,
  onDelete,
  onDuplicate,
}: Readonly<Props>): ReactElement {
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const t = useTranslate('interface', 'components.elementContextBarComponent')
  const themeName = useSelector<State, string>((state) => state.getIn(['shop', 'theme', 'name']))

  const backgroundColorSettings = block.get('backgroundColorSettings')
  const legacyBackgroundColorSetting = block.get('backgroundColor')

  const initialBackgroundColorSettings = useMemo(() => {
    return defaults(
      {},
      getPlain(backgroundColorSettings),
      { backgroundColor: legacyBackgroundColorSetting },
      defaultBackgroundColorSettings,
    )
  }, [backgroundColorSettings, legacyBackgroundColorSetting])

  const [currentBackgroundColorSettings, setCurrentBackgroundColorSettings] =
    useState<BackgroundColorSettings>(initialBackgroundColorSettings)

  const backgroundWidthSettings = block.get('backgroundWidthSettings')

  const initialBackgroundWidthSettings = useMemo(() => {
    const storedSettings = getPlain(backgroundWidthSettings) || {}
    const themeDefaults = getDefaultBackgroundWidthSettings(themeName)

    // If user has never modified the setting, or if it's not marked as user-modified,
    // use the theme default
    if (!storedSettings.isUserModified) {
      return {
        ...themeDefaults,
        isUserModified: false,
      }
    }

    // If user has modified it, keep their setting
    return defaults({}, storedSettings, themeDefaults)
  }, [backgroundWidthSettings, themeName])

  const [currentBackgroundWidthSettings, setCurrentBackgroundWidthSettings] =
    useState<BackgroundWidthSettings>(initialBackgroundWidthSettings)

  // Update width settings when theme changes (only if not user-modified)
  useEffect(() => {
    if (!currentBackgroundWidthSettings.isUserModified) {
      const themeDefaults = getDefaultBackgroundWidthSettings(themeName)
      setCurrentBackgroundWidthSettings({
        ...themeDefaults,
        isUserModified: false,
      })
    }
  }, [themeName, currentBackgroundWidthSettings.isUserModified])

  const {
    backgroundColor,
    gradientStartColor,
    gradientMidColor,
    gradientEndColor,
    gradientOrientation,
    colorOrGradient,
    showGradientMidColor,
  } = currentBackgroundColorSettings

  const { width, isUserModified } = currentBackgroundWidthSettings

  const saveBackground = () => {
    onChange(
      block
        .set('backgroundColorSettings', {
          backgroundColor,
          gradientStartColor,
          gradientMidColor,
          gradientEndColor,
          gradientOrientation,
          colorOrGradient,
          showGradientMidColor,
        })
        .set('backgroundWidthSettings', {
          width,
          isUserModified,
        }),
    )
  }

  const blockRef = useRef<HTMLElement>(undefined)
  const { ref: inViewRef, inView } = useInView({ triggerOnce: true })

  // Use `useCallback` so we don't recreate the function on each render
  const setRefs = useCallback(
    (node) => {
      // Ref's from useRef needs to have the node assigned to `current`
      blockRef.current = node
      // Callback refs, like the one from `useInView`, is a function that takes the node as an argument
      inViewRef(node)
    },
    [inViewRef],
  )

  const [animation, setAnimation] = useState<BlockAnimation>(block.get('blockAnimation', 'none'))
  const [isAnimatingInEditor, setIsAnimatingInEditor] = useState(false)
  const handleAnimationChange = (animation: BlockAnimation) => {
    setAnimation(animation)
    return handleAnimationReplay()
  }
  const handleAnimationReplay = () =>
    new Promise<void>((resolve) => {
      if (blockRef.current) {
        setIsAnimatingInEditor(true)
        blockRef.current.addEventListener(
          'animationend',
          () => {
            setIsAnimatingInEditor(false)
            resolve()
          },
          { once: true },
        )
      } else resolve()
    })
  const saveAnimationChange = () => onChange(block.set('blockAnimation', animation))

  const actionBarRef = useRef<HTMLDivElement>(null)

  // Build a string representation of the block structure. Examples:
  // epages.image
  // image+text-image+text-image+text
  const blockType: string = (block.getIn(['data', 'columns']) || List()).reduce(
    (blockType: string, column: ImmutableMap) =>
      (blockType && blockType + '-') + (column.has('type') ? column.get('type') : column.keySeq().toJS().join('+')),
    '',
  )

  const BlockComponent = blockTypes[block.get('type')]
  if (!BlockComponent) return <div>{'Unknown block type "' + block.get('type') + '"'}</div>

  const hasHeadlineElement = blockSupportsHeadline(block.get('data'))
  const hasHiddenHeadline = isHeadlineHidden(block.get('data'))

  function handleHeadlineToggleButtonClick() {
    if (block.hasIn(['data', 'headline'])) {
      onChange(block.setIn(['data', 'headline', 'data', 'isHidden'], !hasHiddenHeadline))
    } else {
      // Existing shops do not have the "headline" property in the data.
      const initialHeadline = fromJS(emptyHeadline).setIn(['data', 'isHidden'], !hasHiddenHeadline)
      onChange(block.setIn(['data', 'headline'], initialHeadline))
    }
  }

  const isCenterAligned = block.getIn(['alignment'])

  function handleAlignmentToggleButtonClick() {
    if (block.hasIn(['alignment'])) {
      onChange(block.setIn(['alignment'], !isCenterAligned))
    } else {
      onChange(block.setIn(['alignment'], true))
    }
  }

  const handleDataChange = (data: unknown) => onChange(block.set('data', data))

  const showAlignmentButton = (blockType: string): boolean => {
    const allowedTypes = ['epages.text', 'epages.image', 'epages.video', 'epages.product', 'epages.contactform']

    // For single column blocks
    if (
      blockType === 'epages.text' ||
      blockType === 'epages.image' ||
      blockType === 'epages.video' ||
      blockType === 'epages.product' ||
      blockType === 'epages.contactform' ||
      blockType === 'epages.productslider' ||
      blockType === 'epages.imagegallery'
    ) {
      return false
    }

    // For combination blocks (like image+text, video+text), check if any part matches
    return allowedTypes.some((allowedType) => blockType.includes(allowedType))
  }

  const handleBackgroundCancel = () => {
    setCurrentBackgroundColorSettings(initialBackgroundColorSettings)
    setCurrentBackgroundWidthSettings(initialBackgroundWidthSettings)
  }

  const handleWidthSettingsChange = (newSettings: BackgroundWidthSettings) => {
    setCurrentBackgroundWidthSettings(newSettings)
  }

  const handleDuplicate = () => {
    let newBlock = block.set('_id', uuidv4())

    // If it's a product slider specifically, ensure the ID is updated
    if (block.get('type') === 'multi-column') {
      const columns = newBlock.getIn(['data', 'columns'])
      if (columns) {
        const updatedColumns = columns.map((column) => {
          if (column.get('type') === 'epages.productslider' && column.hasIn(['data', 'id'])) {
            return column.setIn(['data', 'id'], uuidv4())
          }
          return column
        })
        newBlock = newBlock.setIn(['data', 'columns'], updatedColumns)
      }
    }

    onDuplicate(newBlock, blockPosition + 1)
  }

  return (
    <div
      id={`block-${block.get('_id')}`}
      data-testid={`block-${block.get('_id')}`}
      className={cc([
        'dali-block',
        {
          'block-background': backgroundColor !== 'transparent' || colorOrGradient === 'gradient',
          'drag-active': isDragActive,
          // Only animate when explicitly triggered (editor) / when in view (storefront)
          [animation]: animation !== 'none' && (editorView ? isAnimatingInEditor : inView),
          'will-fade': animation !== 'none' && !editorView, // avoid flash of content before animation starts (storefront)
          'has-animation': animation !== 'none' && editorView, // for styling purposes (editor)
          'center-alignment': isCenterAligned,
          'background-width-content': width === 'content',
          'background-width-full': width === 'full',
        },
      ])}
      data-block-type={blockType}
      style={{
        backgroundColor: colorOrGradient === 'color' ? backgroundColor : undefined,
        backgroundImage:
          colorOrGradient === 'color'
            ? undefined
            : showGradientMidColor
              ? `linear-gradient( ${gradientOrientation}, ${gradientStartColor}, ${gradientMidColor}, ${gradientEndColor})`
              : `linear-gradient( ${gradientOrientation}, ${gradientStartColor}, ${gradientEndColor})`,
        backgroundOrigin: 'border-box',
      }}
      ref={setRefs}
    >
      {editorView && (
        <div className="dali-block-actionbar-wrapper">
          <div className="wrapper">
            <div ref={actionBarRef} className="dali-block-actionbar">
              {!confirmingDelete && (
                <DragSource
                  type="dali-add"
                  className="dali-block-actionbar-button dali-block-actionbar-button-move"
                  title={t('moveButton.label')}
                  onDrag={() => block}
                  blockPosition={blockPosition}
                  createPreviewNode={() => {
                    const { width = 10 } = actionBarRef.current?.getBoundingClientRect() || {}

                    const div = document.createElement('div')
                    div.className = 'dali-block-actionbar'
                    div.style.width = `${width}px`
                    div.style.visibility = 'visible'
                    div.style.opacity = '1'

                    const div2 = document.createElement('div')
                    div2.className = 'dali-block-actionbar-button dali-block-actionbar-button-move'
                    div.append(div2)

                    return div
                  }}
                  previewNodeOffset={() => {
                    const { width = 10, height = 10 } = actionBarRef.current?.getBoundingClientRect() || {}

                    return { x: width / 2, y: height / 2 }
                  }}
                />
              )}

              {confirmingDelete && <span>{t('deleteButton.warningMessage')}</span>}
              {confirmingDelete && (
                <button
                  className="dali-block-actionbar-button dali-block-actionbar-button-delete-confirm"
                  onClick={onDelete}
                >
                  <span>{t('deleteConfirmButton.label')}</span>
                </button>
              )}
              {confirmingDelete && (
                <button
                  className="dali-block-actionbar-button dali-block-actionbar-button-delete-cancel"
                  onClick={() => setConfirmingDelete(false)}
                >
                  <span>{t('deleteDismissButton.label')}</span>
                </button>
              )}
              {!confirmingDelete && <div className="dali-block-actionbar-button-separator"></div>}
              {!confirmingDelete && (
                <BlockBackgroundSettings
                  currentColorSettings={currentBackgroundColorSettings}
                  currentWidthSettings={currentBackgroundWidthSettings}
                  onColorSettingsChange={setCurrentBackgroundColorSettings}
                  onWidthSettingsChange={handleWidthSettingsChange}
                  onCancel={handleBackgroundCancel}
                  onSave={saveBackground}
                  t={t}
                  themeName={themeName}
                />
              )}

              {!confirmingDelete && (
                <BlockAnimationSettings
                  animation={animation}
                  initialAnimation={block.get('blockAnimation', 'none')}
                  handleAnimationChange={handleAnimationChange}
                  handleAnimationReplay={handleAnimationReplay}
                  saveAnimationChange={saveAnimationChange}
                  setAnimation={setAnimation}
                  t={t}
                />
              )}
              {!confirmingDelete && hasHeadlineElement && (
                <button
                  className={cc({
                    'dali-block-actionbar-button dali-block-actionbar-button-headline': true,
                    'dali-block-actionbar-button-headline-active': !hasHiddenHeadline,
                  })}
                  onClick={handleHeadlineToggleButtonClick}
                  title={t(hasHiddenHeadline ? 'showHeadlineButton.label' : 'hideHeadlineButton.label')}
                />
              )}
              {!confirmingDelete && showAlignmentButton(blockType) && (
                <button
                  className={cc({
                    'dali-block-actionbar-button dali-block-actionbar-button-alignment': true,
                    'dali-block-actionbar-button-alignment-active': isCenterAligned,
                  })}
                  onClick={handleAlignmentToggleButtonClick}
                  title={t(isCenterAligned ? 'startAlignmentButton.label' : 'centerAlignmentButton.label')}
                />
              )}
              {!confirmingDelete && blockType !== 'ng.category' && (
                <button
                  className="dali-block-actionbar-button dali-block-actionbar-button-duplicate"
                  title={t('duplicateButton.label')}
                  onClick={handleDuplicate}
                />
              )}
              {!confirmingDelete && (
                <button
                  className="dali-block-actionbar-button dali-block-actionbar-button-delete"
                  title={t('deleteButton.label')}
                  onClick={() => setConfirmingDelete(true)}
                />
              )}
            </div>
          </div>
        </div>
      )}
      <BlockComponent
        config={config}
        editorView={editorView}
        data={block.get('data')}
        onDataChange={handleDataChange}
        settings={block.get('settings')}
      />
    </div>
  )
}
